import React, { useEffect, useMemo, useState } from "react";
import "../opportunities/OpportunityWorkspace.css";
import type { ActionBrief } from "../../lib/actionBriefs";
import { supabase } from "../../lib/supabase";
import Tooltip from "../Tooltip";
import OpportunityActionBriefPanel from "../opportunities/OpportunityActionBriefPanel";
import { buildContentDraftOutputModel } from "../../lib/currentAssetIntelligence/contentDraftOutputModel";
import ContentBriefAction from "../opportunities/ContentBriefAction";
import { OpportunityWorkspaceHeader } from "../opportunities/OpportunityPresentation";

type LocalOpportunity = {
  id?: string;
  opportunity_type: string;
  opportunity_title: string;
  priority: string;
  action_type: string;
  supporting_score: string | number | null;
  opportunity_description: string;
  recommended_action?: string | null;
  entity_name?: string | null;
  evidence?: string[];
  source_kind?: "local_action" | "entity_builder_recommendation";
  brief_enabled?: boolean;
  action_brief_id?: string | null;
  action_brief_status?: string | null;
};

type LocalCompletedBrief = Partial<ActionBrief> & { id: string };

type EntityBuilderRecommendation = {
  id: string;
  recommendation_type: string;
  entity_area?: string | null;
  entity_name?: string | null;
  title: string;
  summary?: string | null;
  why_it_matters?: string | null;
  recommended_action?: string | null;
  activation_type: string;
  priority: string;
  confidence_score?: string | number | null;
  evidence?: Record<string, any> | any[] | null;
  source_context?: Record<string, any> | null;
  created_at?: string | null;
};

type LocalAccountContext = {
  account_name?: string | null;
  display_name?: string | null;
  business_name?: string | null;
  category?: string | null;
  market?: string | null;
  primary_geo_value?: string | null;
  local_config?: Record<string, any> | null;
};

type CompletionSummary = {
  currentRun: number;
  last30: number;
  ytd: number;
  allTime: number;
  currentYear: number;
};

type CompletedLocalWork = {
  id: string;
  title?: string | null;
  action_type?: string | null;
  marked_complete_at?: string | null;
  completed_by?: string | null;
};

type Props = {
  accountId: string | null;
  presentation?: "full" | "focused";
  initialVisibleCount?: number;
};

const LOCAL_OPPORTUNITY_DISPLAY_LIMIT = 25;

const PRIORITY_RANK: Record<string, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

type LocalOpportunitySortMode = "priority" | "category" | "action_type";

function getPriorityRank(priority: string | null | undefined) {
  return PRIORITY_RANK[String(priority || "").toLowerCase()] ?? 3;
}

function sortLocalOpportunities(
  items: LocalOpportunity[],
  mode: LocalOpportunitySortMode
) {
  const next = [...items];

  return next.sort((a, b) => {
    if (mode === "category") {
      const categoryCompare = String(a.opportunity_type || "").localeCompare(
        String(b.opportunity_type || "")
      );
      if (categoryCompare !== 0) return categoryCompare;
    }

    if (mode === "action_type") {
      const actionCompare = String(a.action_type || "").localeCompare(
        String(b.action_type || "")
      );
      if (actionCompare !== 0) return actionCompare;
    }

    const priorityCompare = getPriorityRank(a.priority) - getPriorityRank(b.priority);
    if (priorityCompare !== 0) return priorityCompare;

    return String(a.opportunity_title || "").localeCompare(
      String(b.opportunity_title || "")
    );
  });
}

function sortFocusedLocalOpportunities(items: LocalOpportunity[]) {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const priorityDifference = getPriorityRank(left.item.priority) - getPriorityRank(right.item.priority);
      if (priorityDifference !== 0) return priorityDifference;
      const leftText = [left.item.opportunity_title, left.item.opportunity_description, left.item.recommended_action, ...(left.item.evidence || [])].filter(Boolean).join(" ").toLowerCase();
      const rightText = [right.item.opportunity_title, right.item.opportunity_description, right.item.recommended_action, ...(right.item.evidence || [])].filter(Boolean).join(" ").toLowerCase();
      const leftSignalScore = (/(repeat|repeated|gap|missing|absent)/.test(leftText) ? 2 : 0) + (/(evidence|proof|output|brief|recommend)/.test(leftText) ? 1 : 0);
      const rightSignalScore = (/(repeat|repeated|gap|missing|absent)/.test(rightText) ? 2 : 0) + (/(evidence|proof|output|brief|recommend)/.test(rightText) ? 1 : 0);
      if (leftSignalScore !== rightSignalScore) return rightSignalScore - leftSignalScore;
      return left.index - right.index;
    })
    .map(({ item }) => item);
}

function getLocalActionClassification(opportunity: LocalOpportunity): LocalActionTone & { key: string | null } {
  const combined = [
    opportunity.action_type,
    opportunity.opportunity_type,
    opportunity.opportunity_title,
    opportunity.recommended_action,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (combined.includes("suppress") || combined.includes("false positive") || combined.includes("cleanup") || combined.includes("hide")) {
    return { key: "Cleanup", card: { border: "1px solid #fecaca", background: "#fff1f2" }, textColor: "#991b1b" };
  }
  if (combined.includes("review") || combined.includes("governance") || combined.includes("alias")) {
    return { key: "Review", card: { border: "1px solid #fed7aa", background: "#fff7ed" }, textColor: "#9a3412" };
  }
  if (combined.includes("entity") || combined.includes("schema") || combined.includes("clarity")) {
    return { key: "Entity", card: { border: "1px solid #ddd6fe", background: "#f5f3ff" }, textColor: "#5b21b6" };
  }
  if (combined.includes("content") || combined.includes("page") || combined.includes("faq") || combined.includes("proof")) {
    return { key: "Content", card: { border: "1px solid #bbf7d0", background: "#f0fdf4" }, textColor: "#166534" };
  }
  if (combined.includes("comparison") || combined.includes("competitor") || combined.includes("competitive")) {
    return { key: "Comparison", card: { border: "1px solid #bae6fd", background: "#f0f9ff" }, textColor: "#0369a1" };
  }
  return { key: null, card: { border: "1px solid #e2e8f0", background: "#f8fafc" }, textColor: "#475569" };
}

function getLocalActionFilterKey(opportunity: LocalOpportunity) {
  return getLocalActionClassification(opportunity).key;
}

function getLocalGroupLabel(opportunity: LocalOpportunity, mode: LocalOpportunitySortMode) {
  if (mode === "priority") return `${formatActionType(opportunity.priority)} priority`;
  if (mode === "category") return formatActionType(opportunity.opportunity_type || "Local opportunity");
  return formatActionType(opportunity.action_type || "Recommended action");
}

function getLocalGroupDescription(mode: LocalOpportunitySortMode) {
  if (mode === "priority") return "Priority is tiered; items in the same tier are not strictly ranked unless a score is shown.";
  if (mode === "category") return "Category groups work by the business or visibility area involved.";
  return "Action type groups work by the kind of implementation required.";
}

const EMPTY_COMPLETION_SUMMARY: CompletionSummary = {
  currentRun: 0,
  last30: 0,
  ytd: 0,
  allTime: 0,
  currentYear: new Date().getFullYear(),
};

const LOCAL_FOUNDATION_PILLARS = [
  {
    title: "Local entity clarity",
    description:
      "Help AI systems connect the business name, services, locations, service areas, and proof signals into one recognizable local entity.",
  },
  {
    title: "Service-area coverage",
    description:
      "Strengthen pages and answers for priority city, neighborhood, and service combinations where customers are likely to ask for help.",
  },
  {
    title: "Trust and review proof",
    description:
      "Reinforce reviews, ratings, testimonials, certifications, photos, and local credibility signals that support AI recommendations.",
  },
  {
    title: "Actionable local content",
    description:
      "Turn weak prompt categories into concrete pages, FAQs, comparison copy, pricing guidance, and local proof improvements.",
  },
];

const LOCAL_METRIC_TOOLTIPS: Record<string, string> = {
  Actions:
    "The number of prioritized local actions Toren found for the latest run. These are the items most likely to strengthen local AI visibility and recommendations.",
  "High priority":
    "High-priority actions are the local fixes Toren sees as most urgent based on visibility gaps, intent coverage, and entity/local proof weaknesses.",
  "Entity signals":
    "Entity signals are actions connected to helping AI systems understand the business, its locations, services, service areas, and proof signals as one local entity.",
  "Content actions":
    "Content actions are page, FAQ, comparison, or local proof improvements that help answer the questions AI systems and buyers are already asking.",
};

function formatActionType(value: string | null | undefined) {
  return String(value || "Action")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getOpportunityTitleKey(value: string | null | undefined) {
  return String(value || "")
    .replace(/^Action Brief:\s*/i, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
}

function getCompletedBriefOpportunity(brief: LocalCompletedBrief): LocalOpportunity {
  const sourceContext = (brief.source_context || {}) as Record<string, any>;
  const sourceOpportunity = sourceContext.opportunity || {};
  const title = sourceOpportunity.title || brief.title || "Completed local action";

  return {
    id: brief.id,
    opportunity_type: sourceOpportunity.category || "completed_local_action",
    opportunity_title: title,
    priority: sourceOpportunity.priority || "Completed",
    action_type: brief.action_type || sourceOpportunity.intent || "completed_action",
    supporting_score: null,
    opportunity_description: sourceOpportunity.summary || "This local Action Brief was completed for the current run.",
    recommended_action: sourceOpportunity.recommendation || null,
    evidence: sourceOpportunity.evidence || [],
    source_kind: "local_action",
    brief_enabled: true,
  };
}

function formatRecommendationType(value: string | null | undefined) {
  return String(value || "entity recommendation")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildEntityRecommendationDescription(item: EntityBuilderRecommendation) {
  const summary = String(item.summary || "").trim();
  const whyItMatters = String(item.why_it_matters || "").trim();

  if (summary && whyItMatters && summary !== whyItMatters) {
    return `${summary} ${whyItMatters}`;
  }

  if (summary) return summary;
  if (whyItMatters) return whyItMatters;

  const entityName = item.entity_name ? ` for ${item.entity_name}` : "";
  return `Agent Smith found an entity or local visibility opportunity${entityName} that needs operator review before it should influence scoring, content, or competitive positioning.`;
}

function buildEntityRecommendationAction(item: EntityBuilderRecommendation) {
  const recommendedAction = String(item.recommended_action || "").trim();

  if (recommendedAction) return recommendedAction;

  const entityName = item.entity_name ? ` ${item.entity_name}` : "";
  const activationType = String(item.activation_type || "").toLowerCase();

  if (activationType.includes("review_entity_mapping")) {
    return `Review and classify${entityName || " this entity"} so Toren knows whether it is self, a direct competitor, an adjacent competitor, a platform/context entity, or should be suppressed.`;
  }

  if (activationType.includes("comparison")) {
    return `Create or strengthen comparison proof${entityName ? ` against ${item.entity_name}` : ""} using verified claims, trust signals, and clear local positioning.`;
  }

  if (activationType.includes("entity")) {
    return "Create or strengthen the primary entity brief so AI systems can understand the business name, location, category, proof, and relationship to nearby entities.";
  }

  return "Review this recommendation and decide whether to create an action brief, update entity governance, or dismiss it.";
}

function extractEntityEvidence(item: EntityBuilderRecommendation) {
  const evidence: string[] = [];

  if (item.entity_name) evidence.push(`Entity: ${item.entity_name}`);
  if (item.entity_area) evidence.push(`Entity area: ${formatRecommendationType(item.entity_area)}`);
  if (item.recommendation_type) {
    evidence.push(`Recommendation type: ${formatRecommendationType(item.recommendation_type)}`);
  }
  if (item.confidence_score !== null && item.confidence_score !== undefined) {
    evidence.push(`Confidence: ${item.confidence_score}`);
  }

  return evidence;
}

function mapEntityBuilderRecommendation(item: EntityBuilderRecommendation): LocalOpportunity {
  const actionType = item.activation_type || item.recommendation_type || "review_entity_opportunity";

  return {
    id: item.id,
    opportunity_type: item.entity_area || item.recommendation_type || "entity_builder",
    opportunity_title: item.title || formatRecommendationType(item.recommendation_type),
    priority: item.priority || "Medium",
    action_type: actionType,
    supporting_score: item.confidence_score ?? null,
    opportunity_description: buildEntityRecommendationDescription(item),
    recommended_action: buildEntityRecommendationAction(item),
    entity_name: item.entity_name || null,
    evidence: extractEntityEvidence(item),
    source_kind: "entity_builder_recommendation",
    brief_enabled: true,
  };
}

function formatCompletionDate(value?: string | null) {
  if (!value) return "Completed";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Completed";

  return `Completed ${date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
}

function getCompletionWindows() {
  const now = new Date();
  const last30 = new Date(now);
  last30.setDate(now.getDate() - 30);

  const ytdStart = new Date(now.getFullYear(), 0, 1);

  return {
    last30Start: last30.toISOString(),
    ytdStart: ytdStart.toISOString(),
    currentYear: now.getFullYear(),
  };
}

function getPriorityBadgeStyle(priority: string): React.CSSProperties {
  const normalized = String(priority || "").toLowerCase();

  if (normalized === "high") {
    return {
      background: "#fee2e2",
      color: "#991b1b",
      border: "1px solid #fecaca"
    };
  }

  if (normalized === "medium") {
    return {
      background: "#fef3c7",
      color: "#92400e",
      border: "1px solid #fde68a"
    };
  }

  return {
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #bbf7d0"
  };
}

type LocalActionTone = {
  card: React.CSSProperties;
  textColor: string;
};

function getLocalActionTone(opportunity: LocalOpportunity): LocalActionTone {
  const { key: _key, ...tone } = getLocalActionClassification(opportunity);
  return tone;
}

const ACTION_TYPE_COLOR_KEY = [
  {
    label: "Comparison",
    note: "comparison work",
    border: "#bae6fd",
    background: "#f0f9ff",
    color: "#0369a1",
  },
  {
    label: "Entity",
    note: "entity clarity",
    border: "#ddd6fe",
    background: "#f5f3ff",
    color: "#5b21b6",
  },
  {
    label: "Review",
    note: "needs review",
    border: "#fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
  },
  {
    label: "Content",
    note: "content/proof",
    border: "#bbf7d0",
    background: "#f0fdf4",
    color: "#166534",
  },
  {
    label: "Cleanup",
    note: "cleanup/suppression",
    border: "#fecaca",
    background: "#fff1f2",
    color: "#991b1b",
  },
];

function ActionTypeColorKey({ activeFilter, onFilter, counts = {}, compact = false }: { activeFilter: string; onFilter: (value: string) => void; counts?: Record<string, number>; compact?: boolean }) {
  return (
    <div
      style={{
        margin: compact ? "6px 0 12px" : "0 0 14px",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        background: "#ffffff",
        padding: "10px 12px",
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <span
        style={{
          color: "#64748b",
          fontSize: 12,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginRight: 2,
        }}
      >
        Action colors
      </span>

      {ACTION_TYPE_COLOR_KEY.map((item) => (
        <button
          key={item.label}
          type="button"
          title={`${item.label}: ${item.note}. Click to filter the visible local actions.`}
          aria-pressed={activeFilter === item.label}
          disabled={(counts[item.label] || 0) === 0 && activeFilter !== item.label}
          onClick={() => onFilter(activeFilter === item.label ? "all" : item.label)}
          style={{
            border: `1px solid ${item.border}`,
            borderRadius: 999,
            background: item.background,
            color: item.color,
            fontSize: 12,
            fontWeight: 800,
            padding: "5px 8px",
            whiteSpace: "nowrap",
            cursor: "pointer",
            opacity: (counts[item.label] || 0) === 0 && activeFilter !== item.label ? 0.45 : 1,
            boxShadow: activeFilter === item.label ? "0 0 0 2px rgba(14,116,144,0.18)" : "none",
          }}
        >
          {item.label}: {item.note} ({counts[item.label] || 0})
        </button>
      ))}
      {activeFilter !== "all" && (
        <button type="button" onClick={() => onFilter("all")} style={{ border: 0, background: "transparent", color: "#0e7490", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
          Clear filter
        </button>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 22,
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)"
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#155e75",
    margin: 0
  },
  title: {
    fontSize: 22,
    lineHeight: 1.2,
    fontWeight: 800,
    color: "#0f172a",
    margin: "6px 0 0"
  },
  subtitle: {
    maxWidth: 860,
    fontSize: 13,
    lineHeight: 1.55,
    color: "#475569",
    margin: "8px 0 18px"
  },
  validationBox: {
    marginBottom: 18,
    border: "1px solid #bbf7d0",
    borderRadius: 18,
    padding: 16,
    background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)",
  },
  validationHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "flex-start",
    marginBottom: 13,
  },
  validationEyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#166534",
    margin: 0,
  },
  validationText: {
    margin: "5px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.45,
    maxWidth: 780,
  },
  validationMetricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 10,
  },
  validationMetric: {
    border: "1px solid #bbf7d0",
    borderRadius: 14,
    background: "#ffffff",
    padding: "11px 13px",
  },
  validationMetricValue: {
    color: "#166534",
    fontSize: 23,
    fontWeight: 900,
    lineHeight: 1,
  },
  validationMetricLabel: {
    marginTop: 5,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },
  validationChips: {
    marginTop: 12,
    borderTop: "1px solid #bbf7d0",
    paddingTop: 12,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  validationChip: {
    border: "1px solid #bbf7d0",
    borderRadius: 999,
    background: "#ffffff",
    padding: "7px 11px",
    color: "#166534",
    fontSize: 12,
    fontWeight: 800,
  },
  validationError: {
    color: "#92400e",
    fontSize: 12,
    fontWeight: 800,
  },
  recentWorkBox: {
    marginTop: 13,
    borderTop: "1px solid #bbf7d0",
    paddingTop: 13,
  },
  recentWorkTitle: {
    margin: "0 0 9px",
    color: "#166534",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  recentWorkList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 9,
  },
  recentWorkItem: {
    border: "1px solid #bbf7d0",
    borderRadius: 14,
    background: "#ffffff",
    padding: "10px 12px",
  },
  recentWorkItemTitle: {
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 850,
    lineHeight: 1.35,
    margin: 0,
  },
  recentWorkMeta: {
    marginTop: 6,
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    color: "#64748b",
    fontSize: 11,
    fontWeight: 750,
    lineHeight: 1.3,
  },
  foundationBox: {
    marginBottom: 18,
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    padding: 16,
    background: "linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%)",
  },
  foundationHeader: {
    display: "block",
  },
  foundationTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 800,
  },
  foundationText: {
    margin: "7px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
    maxWidth: "100%",
  },
  foundationMetricGrid: {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
  },
  foundationMetric: {
    border: "1px solid #ccfbf1",
    borderRadius: 14,
    background: "#ffffff",
    padding: "10px 12px",
  },
  foundationMetricValue: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 900,
    lineHeight: 1,
  },
  foundationMetricLabel: {
    display: "inline-flex",
    marginTop: 5,
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
    alignItems: "center",
    gap: 6,
  },
  pillarGrid: {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
  },
  pillar: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    background: "#ffffff",
    padding: 13,
  },
  pillarTitle: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 5,
  },
  pillarDescription: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.45,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 14
  },
  item: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 18,
    background: "#ffffff"
  },
  itemHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 18,
    flexWrap: "wrap"
  },
  itemMain: {
    flex: "1 1 620px",
    minWidth: 0
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 8
  },
  itemTitle: {
    fontSize: 15,
    lineHeight: 1.3,
    fontWeight: 800,
    color: "#0f172a",
    margin: 0
  },
  itemDescription: {
    fontSize: 13,
    lineHeight: 1.6,
    color: "#475569",
    margin: 0,
    maxWidth: 980
  },
  recommendedAction: {
    fontSize: 13,
    lineHeight: 1.6,
    color: "#334155",
    margin: "8px 0 0",
    maxWidth: 980
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 82,
    padding: "5px 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    textAlign: "center"
  },
  metaBox: {
    flex: "0 0 190px",
    borderRadius: 12,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    padding: "10px 12px"
  },
  metaLabel: {
    display: "block",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginBottom: 4
  },
  actionType: {
    display: "block",
    fontSize: 12,
    color: "#475569",
    fontWeight: 700,
    lineHeight: 1.35
  },
  empty: {
    fontSize: 13,
    color: "#64748b",
    margin: 0
  }
};

function FocusedLocalWorkSummary({
  title,
  action,
  outputType,
  targetAsset,
  audience,
  evidencePattern,
  expectedResult,
  draftStatus,
  accountId,
  runId,
  recommendationId,
  recommendationKey,
  recommendationType,
  targetEntity,
  recommendedOutput,
  confidence,
  liveCaiEnabled,
  actionBriefId,
  accountType,
  entitlementOverride,
  accountName,
  whyPoints,
}: {
  title: string;
  action: string;
  outputType: string;
  targetAsset: string;
  audience: string;
  evidencePattern: string;
  expectedResult: string;
  draftStatus: string;
  accountId: string;
  runId?: string | null;
  recommendationId?: string | null;
  recommendationKey?: string | null;
  recommendationType?: string | null;
  targetEntity?: string | null;
  recommendedOutput?: string | null;
  confidence?: string | number | null;
  liveCaiEnabled?: boolean;
  actionBriefId?: string | null;
  accountType?: string | null;
  entitlementOverride?: unknown;
  accountName?: string | null;
  whyPoints: string[];
}) {
  return (
    <div
      data-testid="oe-next-focused-work"
      style={{
        marginTop: 10,
        padding: "12px 14px",
        border: "1px solid #bae6fd",
        borderRadius: 14,
        background: "#f0f9ff",
        order: 1,
      }}
    >
      <p style={{ margin: 0, color: "#0369a1", fontSize: 11, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        Next focused work
      </p>
      <p style={{ margin: "7px 0 0", color: "#0f172a", fontSize: 16, fontWeight: 900 }}>
        Recommended focus: {title}
      </p>
      <p data-testid="oe-recommended-output" style={{ margin: "7px 0 0", color: "#334155", fontSize: 13, lineHeight: 1.45 }}>
        <strong>Recommended output:</strong> {outputType}<br />
        <strong>Target asset:</strong> {targetAsset}<br />
        <strong>For:</strong> {audience}
      </p>
      <div data-testid="oe-why-this-next" style={{ marginTop: 7, color: "#334155", fontSize: 12, lineHeight: 1.4 }}>
        <strong>Why this is next:</strong>
        <div style={{ marginTop: 3 }}>{evidencePattern}</div>
        <ul style={{ margin: "3px 0 0", paddingLeft: 16 }}>{whyPoints.slice(0, 2).map((point) => <li key={point}>{point}</li>)}</ul>
      </div>
      <p data-testid="oe-expected-result" style={{ margin: "7px 0 0", color: "#334155", fontSize: 12, lineHeight: 1.4 }}><strong>Expected result:</strong> {expectedResult}</p>
      <p style={{ margin: "7px 0 0", color: "#0f766e", fontSize: 13, fontWeight: 800 }}>
        <strong>Recommended next step:</strong> Review opportunity · {action}
      </p>
      <LocalFutureOutputIndicators draftStatus={draftStatus} />
      <ContentBriefAction
        accountId={accountId || ""}
        runId={runId}
        recommendationId={recommendationId}
        recommendationKey={recommendationKey || recommendationId}
        recommendationType={recommendationType}
        targetEntity={targetEntity}
        recommendedOutput={recommendedOutput}
        confidence={confidence}
        opportunityCategory="asset"
        liveCaiEnabled={liveCaiEnabled}
        actionBriefId={actionBriefId}
        actionBriefReady={draftStatus === "Ready for draft" || draftStatus === "Draft prepared"}
        accountType={accountType || "local"}
        entitlementOverride={entitlementOverride}
        productArea="local"
        accountName={accountName}
        recommendationTitle={title}
        targetAsset={targetAsset}
        targetAudience={audience}
        evidencePattern={evidencePattern}
        objective={expectedResult}
        expectedResult={expectedResult}
      />
    </div>
  );
}

function getLocalPrescribedOutput(opportunity: LocalOpportunity, accountId?: string | null, runId?: string | null) {
  return buildContentDraftOutputModel({
    recommendation: opportunity,
    recommendationId: opportunity.id,
    accountId,
    runId,
    actionBriefId: opportunity.action_brief_id,
    actionBriefStatus: opportunity.action_brief_status,
    title: opportunity.opportunity_title,
    actionType: opportunity.action_type,
    description: opportunity.opportunity_description,
    recommendedAction: opportunity.recommended_action,
  });
}

function LocalFutureOutputIndicators({ draftStatus }: { draftStatus: string }) {
  const draftHelper = draftStatus === "Ready for draft" || draftStatus === "Draft prepared"
    ? "Ready for draft · generation not enabled yet"
    : "Requires reviewed action brief";
  return <details data-testid="oe-future-output-indicators" style={{ marginTop: 10 }}><summary>Future draft delivery — not available</summary><div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
    <button type="button" disabled aria-disabled="true" title={draftHelper} style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "5px 8px", background: "#f8fafc", color: "#64748b", fontSize: 11, fontWeight: 800, cursor: "not-allowed" }}>Generate Content Draft</button>
    <span style={{ color: "#64748b", fontSize: 11, alignSelf: "center" }}>{draftHelper}</span>
    <button type="button" disabled aria-disabled="true" title="Draft delivery not connected yet." style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "5px 8px", background: "#f8fafc", color: "#64748b", fontSize: 11, fontWeight: 800, cursor: "not-allowed" }}>Prepare WordPress Draft · Coming soon</button>
    <button type="button" disabled aria-disabled="true" title="Draft delivery not connected yet." style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "5px 8px", background: "#f8fafc", color: "#64748b", fontSize: 11, fontWeight: 800, cursor: "not-allowed" }}>Prepare Shopify Draft · Coming soon</button>
  </div></details>;
}

function FocusedSecondaryLocalRow({
  opportunity,
  selected,
  onSelect,
  accountId,
  localRunId,
  accountContext,
  onCompletionChange,
}: {
  opportunity: LocalOpportunity;
  selected: boolean;
  onSelect: () => void;
  accountId: string | null;
  localRunId: string | null;
  accountContext: LocalAccountContext | null;
  onCompletionChange: (brief: LocalCompletedBrief) => void;
}) {
  const output = getLocalPrescribedOutput(opportunity, accountId, localRunId);
  return <div data-testid="oe-secondary-opportunity" style={{ border: "1px solid #dbeafe", borderRadius: 12, padding: "10px 12px", background: "#ffffff" }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
      <div>
        <div style={{ color: "#0f172a", fontSize: 13, fontWeight: 850 }}>{opportunity.opportunity_title}</div>
        <div style={{ marginTop: 3, color: "#64748b", fontSize: 11 }}>{opportunity.priority || "Priority unavailable"} · {output.outputType}</div>
      </div>
      <button type="button" onClick={onSelect} aria-expanded={selected} style={{ border: "1px solid #93c5fd", borderRadius: 999, padding: "5px 9px", background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>{selected ? "Hide" : "Select"}</button>
    </div>
    {selected && <div style={{ marginTop: 9, borderTop: "1px solid #dbeafe", paddingTop: 9 }}>
      <div style={{ color: "#475569", fontSize: 12, lineHeight: 1.45 }}>{opportunity.opportunity_description}</div>
      {opportunity.recommended_action && <div style={{ marginTop: 6, color: "#0369a1", fontSize: 12, fontWeight: 800 }}>{opportunity.recommended_action}</div>}
      {opportunity.brief_enabled !== false && (
        <OpportunityActionBriefPanel
          accountId={accountId || ""}
          runId={localRunId}
          productArea="local"
          opportunity={{
            title: opportunity.opportunity_title,
            summary: opportunity.opportunity_description,
            recommendation: opportunity.recommended_action || undefined,
            category: opportunity.opportunity_type,
            intent: opportunity.action_type,
            priority: opportunity.priority,
            evidence: [`Supporting score: ${opportunity.supporting_score ?? "Not available"}`, ...(opportunity.evidence || [])],
          }}
          accountContext={{
            accountName: accountContext?.display_name || accountContext?.business_name || accountContext?.account_name || undefined,
            category: accountContext?.category || opportunity.opportunity_type,
            location: accountContext?.primary_geo_value || accountContext?.market || undefined,
            differentiators: Array.isArray(accountContext?.local_config?.differentiators) ? accountContext.local_config.differentiators : undefined,
          }}
          onCompletionChange={onCompletionChange}
        />
      )}
    </div>}
  </div>;
}

export default function LocalOpportunityEngine({
  accountId,
  presentation = "full",
  initialVisibleCount = 3,
}: Props) {
  const isFocused = presentation === "focused";
  const focusedVisibleCount = Math.max(1, Math.min(initialVisibleCount, 5));
  const [showAllRecommendations, setShowAllRecommendations] = useState(false);
  const [selectedFocusedOpportunityKey, setSelectedFocusedOpportunityKey] = useState<string | null>(null);
  const [opportunities, setOpportunities] = useState<LocalOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [accountContext, setAccountContext] = useState<LocalAccountContext | null>(
    null
  );
  const [localRunId, setLocalRunId] = useState<string | null>(null);
  const [localOpportunityTotal, setLocalOpportunityTotal] = useState(0);
  const [completionSummary, setCompletionSummary] = useState<CompletionSummary>(
    EMPTY_COMPLETION_SUMMARY
  );
  const [completionStatus, setCompletionStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );
  const [recentCompletedWork, setRecentCompletedWork] = useState<CompletedLocalWork[]>([]);
  const [completedLocalBriefs, setCompletedLocalBriefs] = useState<LocalCompletedBrief[]>([]);
  const [sortMode, setSortMode] = useState<LocalOpportunitySortMode>("priority");
  const [actionTypeFilter, setActionTypeFilter] = useState("all");

  useEffect(() => {
    async function loadOpportunities() {
      if (!accountId) {
        setOpportunities([]);
        setCompletedLocalBriefs([]);
        setAccountContext(null);
        setLocalRunId(null);
        setLocalOpportunityTotal(0);
        return;
      }

      const { data: accountData, error: accountError } = await supabase
        .from("accounts")
        .select(
          "account_name, display_name, business_name, category, market, primary_geo_value, local_config"
        )
        .eq("account_id", accountId)
        .maybeSingle();

      if (accountError) throw accountError;

      setAccountContext((accountData || null) as LocalAccountContext | null);

      setLoading(true);
      setErrorMessage("");

      try {
        const { data: summaryData, error: summaryError } = await supabase
          .from("toren_local_summary")
          .select("run_id, latest_executed_at")
          .eq("account_id", accountId)
          .order("latest_executed_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (summaryError) throw summaryError;

        if (!summaryData?.run_id) {
          setOpportunities([]);
          setCompletedLocalBriefs([]);
          setLocalRunId(null);
          setLocalOpportunityTotal(0);
          return;
        }

        setLocalRunId(summaryData.run_id);

        const { data, error, count } = await supabase
          .from("toren_local_opportunity_actions")
          .select("*", { count: "exact" })
          .eq("account_id", accountId)
          .eq("run_id", summaryData.run_id)
          .order("sort_order", { ascending: true })
          .limit(LOCAL_OPPORTUNITY_DISPLAY_LIMIT);

        if (error) throw error;

        const localActions = ((data || []) as LocalOpportunity[]).map((item) => ({
          ...item,
          source_kind: "local_action" as const,
          brief_enabled: true,
        }));

        const {
          data: entityRecommendationData,
          error: entityRecommendationError,
          count: entityRecommendationCount,
        } = await supabase
          .from("entity_builder_recommendations")
          .select(
            "id,recommendation_type,entity_area,entity_name,title,summary,why_it_matters,recommended_action,activation_type,priority,confidence_score,evidence,source_context,created_at",
            { count: "exact" }
          )
          .eq("account_id", accountId)
          .eq("run_id", summaryData.run_id)
          .eq("status", "open")
          .order("priority", { ascending: true })
          .order("created_at", { ascending: true })
          .limit(LOCAL_OPPORTUNITY_DISPLAY_LIMIT);

        if (entityRecommendationError) throw entityRecommendationError;

        const entityRecommendations = ((entityRecommendationData || []) as EntityBuilderRecommendation[])
          .map(mapEntityBuilderRecommendation);

        const { data: completedBriefData, error: completedBriefError } = await supabase
          .from("opportunity_action_briefs")
          .select("*")
          .eq("account_id", accountId)
          .eq("run_id", summaryData.run_id)
          .eq("product_area", "local")
          .not("marked_complete_at", "is", null)
          .eq("is_active", true)
          .order("marked_complete_at", { ascending: false });

        if (completedBriefError) throw completedBriefError;

        const combinedOpportunities = sortLocalOpportunities(
          [...localActions, ...entityRecommendations],
          sortMode
        ).slice(0, LOCAL_OPPORTUNITY_DISPLAY_LIMIT);

        setOpportunities(combinedOpportunities);
        setCompletedLocalBriefs((completedBriefData || []) as LocalCompletedBrief[]);
        setLocalOpportunityTotal(
          (count || localActions.length) +
            (entityRecommendationCount || entityRecommendations.length)
        );
      } catch (error: any) {
        console.error("Local opportunity load error:", error);
        setErrorMessage(
          error?.message || "Unable to load local opportunities."
        );
        setCompletedLocalBriefs([]);
      } finally {
        setLoading(false);
      }
    }

    loadOpportunities();
  }, [accountId, sortMode]);

  useEffect(() => {
    let isMounted = true;

    async function countCompletedLocalBriefs({
      gte = null,
      activeRunId = null,
    }: {
      gte?: string | null;
      activeRunId?: string | null;
    }) {
      if (!accountId) return 0;

      let query = supabase
        .from("opportunity_action_briefs")
        .select("id", { count: "exact", head: true })
        .eq("account_id", accountId)
        .eq("product_area", "local")
        .not("marked_complete_at", "is", null);

      if (gte) query = query.gte("marked_complete_at", gte);
      if (activeRunId) query = query.eq("run_id", activeRunId);

      const { count, error } = await query;
      if (error) throw error;

      return count || 0;
    }

    async function loadRecentCompletedWork() {
      if (!accountId) return [];

      const { data, error } = await supabase
        .from("opportunity_action_briefs")
        .select("id,title,action_type,marked_complete_at,completed_by")
        .eq("account_id", accountId)
        .eq("product_area", "local")
        .not("marked_complete_at", "is", null)
        .order("marked_complete_at", { ascending: false })
        .limit(4);

      if (error) throw error;

      return (data || []) as CompletedLocalWork[];
    }

    async function loadCompletionSummary() {
      if (!accountId) {
        setCompletionSummary(EMPTY_COMPLETION_SUMMARY);
        setRecentCompletedWork([]);
        setCompletionStatus("ready");
        return;
      }

      setCompletionStatus((current) => (current === "ready" ? "ready" : "loading"));

      try {
        const windows = getCompletionWindows();
        const [currentRun, last30, ytd, allTime, recentWork] = await Promise.all([
          localRunId
            ? countCompletedLocalBriefs({ activeRunId: localRunId })
            : Promise.resolve(0),
          countCompletedLocalBriefs({ gte: windows.last30Start }),
          countCompletedLocalBriefs({ gte: windows.ytdStart }),
          countCompletedLocalBriefs({}),
          loadRecentCompletedWork(),
        ]);

        if (!isMounted) return;

        setCompletionSummary({
          currentRun,
          last30,
          ytd,
          allTime,
          currentYear: windows.currentYear,
        });
        setRecentCompletedWork(recentWork);
        setCompletionStatus("ready");
      } catch (error) {
        console.warn("[LocalOpportunityEngine] Completed local summary failed", error);
        if (isMounted) {
          setCompletionSummary(EMPTY_COMPLETION_SUMMARY);
          setRecentCompletedWork([]);
          setCompletionStatus("error");
        }
      }
    }

    loadCompletionSummary();
    const refreshTimer = window.setInterval(loadCompletionSummary, 10000);

    return () => {
      isMounted = false;
      window.clearInterval(refreshTimer);
    };
  }, [accountId, localRunId]);

  const localFoundationMetrics = useMemo(() => {
    const highPriority = opportunities.filter(
      (item) => String(item.priority || "").toLowerCase() === "high"
    ).length;

    const entityActions = opportunities.filter((item) =>
      `${item.opportunity_type} ${item.action_type} ${item.opportunity_title}`
        .toLowerCase()
        .includes("entity")
    ).length;

    const contentActions = opportunities.filter((item) =>
      `${item.opportunity_type} ${item.action_type} ${item.opportunity_title}`
        .toLowerCase()
        .includes("content")
    ).length;

    return {
      total: opportunities.length,
      highPriority,
      entityActions,
      contentActions,
    };
  }, [opportunities]);

  const completedTitleKeys = useMemo(
    () => new Set(completedLocalBriefs.map((brief) => {
      const sourceContext = (brief.source_context || {}) as Record<string, any>;
      return getOpportunityTitleKey(sourceContext.opportunity?.title || brief.title);
    })),
    [completedLocalBriefs]
  );

  const activeOpportunities = useMemo(
    () => opportunities.filter((opportunity) => !completedTitleKeys.has(getOpportunityTitleKey(opportunity.opportunity_title))),
    [opportunities, completedTitleKeys]
  );

  const visibleOpportunities = useMemo(
    () => activeOpportunities.filter((opportunity) => actionTypeFilter === "all" || getLocalActionFilterKey(opportunity) === actionTypeFilter),
    [activeOpportunities, actionTypeFilter]
  );

  const localActionCounts = useMemo(
    () => activeOpportunities.reduce<Record<string, number>>((counts, opportunity) => {
      const key = getLocalActionFilterKey(opportunity);
      if (key) counts[key] = (counts[key] || 0) + 1;
      return counts;
    }, {}),
    [activeOpportunities]
  );

  useEffect(() => {
    if (actionTypeFilter !== "all" && visibleOpportunities.length === 0) {
      setActionTypeFilter("all");
    }
  }, [actionTypeFilter, visibleOpportunities.length]);

  const focusedOpportunities = useMemo(
    () => isFocused ? sortFocusedLocalOpportunities(visibleOpportunities) : visibleOpportunities,
    [isFocused, visibleOpportunities]
  );

  const renderedVisibleOpportunities = isFocused && !showAllRecommendations
    ? focusedOpportunities.slice(0, focusedVisibleCount)
    : focusedOpportunities;
  const focusedLocalOpportunity = focusedOpportunities.find(item => (item.id || `${item.opportunity_type}-${item.opportunity_title}`) === selectedFocusedOpportunityKey) || focusedOpportunities[0];
  const focusedLocalOutput = focusedLocalOpportunity
    ? getLocalPrescribedOutput(focusedLocalOpportunity, accountId, localRunId)
    : { outputType: "Manual review", targetAsset: "Target asset unavailable", audience: "The audience most likely to rely on this decision.", evidencePattern: "A reviewed visibility or recommendation gap is driving this prescription.", expectedResult: "A clearer reviewed asset ready for the next human review.", draftStatus: "Not ready" };

  const visibleCompletedOpportunities = useMemo(
    () => completedLocalBriefs
      .map(getCompletedBriefOpportunity)
      .filter((opportunity) => actionTypeFilter === "all" || getLocalActionFilterKey(opportunity) === actionTypeFilter),
    [completedLocalBriefs, actionTypeFilter]
  );
  const localInactiveOrCompletedCount = Math.max(0, (localOpportunityTotal || opportunities.length) - renderedVisibleOpportunities.length) + visibleCompletedOpportunities.length;

  function handleLocalCompletionChange(updatedBrief: LocalCompletedBrief) {
    const opportunity = getCompletedBriefOpportunity(updatedBrief);
    const titleKey = getOpportunityTitleKey(opportunity.opportunity_title);

    if (updatedBrief.marked_complete_at) {
      setCompletedLocalBriefs((current) => [
        updatedBrief,
        ...current.filter((brief) => brief.id !== updatedBrief.id),
      ]);
      setOpportunities((current) => current.filter((item) => getOpportunityTitleKey(item.opportunity_title) !== titleKey));
      return;
    }

    setCompletedLocalBriefs((current) => current.filter((brief) => brief.id !== updatedBrief.id));
    setOpportunities((current) => sortLocalOpportunities(
      current.some((item) => getOpportunityTitleKey(item.opportunity_title) === titleKey)
        ? current
        : [...current, opportunity],
      sortMode
    ).slice(0, LOCAL_OPPORTUNITY_DISPLAY_LIMIT));
  }

  const hasLocalWork = activeOpportunities.length > 0 || visibleCompletedOpportunities.length > 0;

  return (
    <section style={styles.section}>
      {isFocused && <OpportunityWorkspaceHeader title="Local" description="Review local visibility priorities, entity signals and existing action plans." />}
      <div className={isFocused ? "oe-action-layout" : undefined}>
      {isFocused && <nav className="oe-navigator" aria-label="Local opportunities"><strong>Opportunities</strong>{renderedVisibleOpportunities.map(item => { const key = item.id || `${item.opportunity_type}-${item.opportunity_title}`; return <button type="button" className="oe-opportunity" key={key} aria-current={focusedLocalOpportunity === item ? "true" : undefined} onClick={() => setSelectedFocusedOpportunityKey(key)} title={item.opportunity_title}><strong>{item.opportunity_title}</strong><small>{item.opportunity_type}</small></button>; })}</nav>}
      <div style={{ minWidth: 0 }}>
      {!isFocused && <><p style={styles.eyebrow}>Local focus</p>
      <h2 style={styles.title}>Local Opportunity Engine</h2>
      <p style={styles.subtitle}>Prioritized actions based on local AI visibility gaps, intent coverage, and competitor/entity mentions. This section is intended for users responsible for improving results.</p></>}

      {isFocused && focusedLocalOpportunity && (
        <FocusedLocalWorkSummary
          title={focusedLocalOpportunity.opportunity_title}
          action={focusedLocalOpportunity.recommended_action || focusedLocalOpportunity.action_type}
          outputType={focusedLocalOutput.outputType}
          targetAsset={focusedLocalOutput.targetAsset}
          audience={focusedLocalOutput.audience}
          evidencePattern={focusedLocalOutput.evidencePattern}
          expectedResult={focusedLocalOutput.expectedResult}
          draftStatus={focusedLocalOutput.draftStatus}
          accountId={accountId || ""}
          runId={localRunId}
          recommendationId={focusedLocalOpportunity.id}
          recommendationKey={focusedLocalOpportunity.id || focusedLocalOpportunity.opportunity_title}
          recommendationType={focusedLocalOpportunity.opportunity_type}
          targetEntity={focusedLocalOpportunity.entity_name}
          recommendedOutput={focusedLocalOutput.outputType}
          liveCaiEnabled={process.env.NODE_ENV !== "test"}
          actionBriefId={focusedLocalOpportunity.action_brief_id}
          accountType="local"
          entitlementOverride={accountContext?.local_config?.contentBriefEntitlement || accountContext?.local_config?.content_brief_entitlement}
          accountName={accountContext?.display_name || accountContext?.business_name || accountContext?.account_name}
          whyPoints={[
            focusedLocalOpportunity.opportunity_description,
            "It is the clearest first step in the current local opportunity queue.",
            "A reviewed action brief keeps the next move focused.",
          ]}
        />
      )}

      <div style={isFocused ? { display: "flex", flexDirection: "column" } : undefined}>
      <div style={{ ...styles.validationBox, ...(isFocused ? { marginTop: 8, padding: 10, order: 3 } : {}) }}>
        <div style={styles.validationHeader}>
          <div>
            <p style={styles.validationEyebrow}>Completed local opportunity validation</p>
            <p style={styles.validationText}>
              Proof that local optimization opportunities are being completed, not just identified. This keeps execution progress visible across trust, service-area, entity, review, and content improvements.
            </p>
          </div>

          {completionStatus === "error" && (
            <div style={styles.validationError}>Metrics unavailable</div>
          )}
        </div>

        <div style={{ ...styles.validationMetricGrid, ...(isFocused ? { gap: 7 } : {}) }}>
          {[
            ["This run", completionSummary.currentRun],
            ["Last 30 days", completionSummary.last30],
            [`${completionSummary.currentYear} YTD`, completionSummary.ytd],
            ["All time", completionSummary.allTime],
          ].map(([label, value]) => (
            <div key={String(label)} style={styles.validationMetric}>
              <div style={styles.validationMetricValue}>
                {completionStatus === "loading" ? "—" : value}
              </div>
              <div style={styles.validationMetricLabel}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ ...styles.validationChips, ...(isFocused ? { marginTop: 8 } : {}) }}>
          <div style={styles.validationChip}>
            Local Action Briefs: {completionStatus === "loading" ? "—" : completionSummary.allTime}
          </div>
          <div style={styles.validationChip}>
            Current-run recommendations: {activeOpportunities.length}
          </div>
        </div>

      </div>

      <div style={{ ...styles.foundationBox, marginTop: isFocused ? 8 : 0, padding: isFocused ? 11 : 16, ...(isFocused ? { order: 4 } : {}) }}>
        <div style={styles.foundationHeader}>
          <div>
            <p style={styles.eyebrow}>Local AI Presence Foundation</p>
            <h3 style={styles.foundationTitle}>
              Build the local proof AI systems need to recommend the business.
            </h3>
            <p style={styles.foundationText}>
              Local opportunity work is not just fixing missing rankings. It strengthens the business entity, local service coverage, trust signals, and proof that help AI systems understand where and why to recommend the business.
            </p>
          </div>

          <div style={styles.foundationMetricGrid}>
            {[
              ["Actions", localFoundationMetrics.total],
              ["High priority", localFoundationMetrics.highPriority],
              ["Entity signals", localFoundationMetrics.entityActions],
              ["Content actions", localFoundationMetrics.contentActions],
            ].map(([label, value]) => (
              <div key={label} style={styles.foundationMetric}>
                <div style={styles.foundationMetricValue}>{value}</div>
                <span style={styles.foundationMetricLabel}>
                  {label}
                  <Tooltip text={LOCAL_METRIC_TOOLTIPS[String(label)] || "This metric helps explain the current local AI presence-building priority."} />
                </span>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.pillarGrid}>
          {LOCAL_FOUNDATION_PILLARS.map((pillar) => (
            <div key={pillar.title} style={styles.pillar}>
              <div style={styles.pillarTitle}>{pillar.title}</div>
              <div style={styles.pillarDescription}>{pillar.description}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={isFocused ? { order: 2, marginTop: 8 } : undefined}>
      {loading && <p style={styles.empty}>Loading local opportunities…</p>}

      {errorMessage && (
        <p style={{ ...styles.empty, color: "#b91c1c" }}>{errorMessage}</p>
      )}

      {!loading && !errorMessage && !hasLocalWork && (
        <p role="status" style={{ ...styles.empty, border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", padding: "9px 11px" }}>
          No saved Local opportunities are available for the latest completed run.
        </p>
      )}

      {!loading && !errorMessage && activeOpportunities.length > 0 && (
        <div
          style={{
            margin: "0 0 14px",
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
            Showing {renderedVisibleOpportunities.length} active recommendations
            {localInactiveOrCompletedCount > 0 ? ` · ${localInactiveOrCompletedCount} completed or inactive items hidden` : ""}
          </div>

          <label
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              color: "#475569",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em"
            }}
          >
            Sort by
            <select
              value={sortMode}
              onChange={(event) =>
                setSortMode(event.target.value as LocalOpportunitySortMode)
              }
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                padding: "9px 11px",
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                background: "#ffffff"
              }}
            >
              <option value="priority">Priority</option>
              <option value="category">Category</option>
              <option value="action_type">Action type / work type</option>
            </select>
          </label>
        </div>
      )}

      {!loading && !errorMessage && hasLocalWork && (
          <ActionTypeColorKey activeFilter={actionTypeFilter} onFilter={setActionTypeFilter} counts={localActionCounts} compact={isFocused} />
      )}

      {!loading && !errorMessage && activeOpportunities.length > 0 && (
        visibleOpportunities.length === 0 ? (
          <div style={styles.empty}>
            <p style={{ margin: 0 }}>No local actions match the selected action color.</p>
            {actionTypeFilter !== "all" && <button type="button" onClick={() => setActionTypeFilter("all")} style={{ marginTop: 7, border: 0, background: "transparent", color: "#0e7490", fontSize: 12, fontWeight: 800, cursor: "pointer", padding: 0 }}>Clear filter</button>}
          </div>
        ) : (
        <div style={styles.list}>
          {renderedVisibleOpportunities.map((opportunity, index) => {
            const actionTone = getLocalActionTone(opportunity);
            const groupLabel = getLocalGroupLabel(opportunity, sortMode);
            const previousGroupLabel = index > 0 ? getLocalGroupLabel(renderedVisibleOpportunities[index - 1], sortMode) : null;
            const opportunityKey = opportunity.id || `${opportunity.opportunity_type}-${opportunity.opportunity_title}`;
            const isSecondaryFocused = isFocused && opportunityKey !== (focusedLocalOpportunity?.id || `${focusedLocalOpportunity?.opportunity_type}-${focusedLocalOpportunity?.opportunity_title}`);

            return (
            <React.Fragment key={opportunity.id || `${opportunity.opportunity_type}-${opportunity.opportunity_title}`}>
              {groupLabel !== previousGroupLabel && (
                <div style={{ borderTop: index === 0 ? "0" : "1px solid #e2e8f0", paddingTop: index === 0 ? 0 : 14, marginTop: index === 0 ? 0 : 2 }}>
                  <div style={{ color: "#0f172a", fontSize: 13, fontWeight: 900 }}>{groupLabel}</div>
                  <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>{getLocalGroupDescription(sortMode)}</div>
                </div>
              )}
            <div style={{ ...(isSecondaryFocused ? { ...styles.item, border: "0", padding: 0, background: "transparent" } : styles.item), display: isFocused && isSecondaryFocused ? "none" : undefined }}>
              {isSecondaryFocused ? (
                <FocusedSecondaryLocalRow
                  opportunity={opportunity}
                  selected={selectedFocusedOpportunityKey === opportunityKey}
                  onSelect={() => setSelectedFocusedOpportunityKey((current) => current === opportunityKey ? null : opportunityKey)}
                  accountId={accountId}
                  localRunId={localRunId}
                  accountContext={accountContext}
                  onCompletionChange={handleLocalCompletionChange}
                />
              ) : (
              <>
              <div style={styles.itemHeader}>
                <div style={styles.itemMain}>
                  <div style={styles.titleRow}>
                    <p style={styles.itemTitle}>
                      {opportunity.opportunity_title}
                    </p>
                    <span
                      style={{
                        ...styles.badge,
                        ...getPriorityBadgeStyle(opportunity.priority)
                      }}
                    >
                      {opportunity.priority || "Priority"}
                    </span>
                  </div>

                  <p style={styles.itemDescription}>
                    {opportunity.opportunity_description}
                  </p>

                  {opportunity.recommended_action && (
                    <p style={styles.recommendedAction}>
                      <strong>Recommended action:</strong>{" "}
                      {opportunity.recommended_action}
                    </p>
                  )}
                </div>

                <div style={{ ...styles.metaBox, ...actionTone.card }}>
                  <span style={styles.metaLabel}>Action Type</span>
                  <span style={{ ...styles.actionType, color: actionTone.textColor }}>
                    {formatActionType(opportunity.action_type)}
                  </span>
                </div>
              </div>

              {opportunity.brief_enabled !== false && (
                <OpportunityActionBriefPanel
                  accountId={accountId || ""}
                  runId={localRunId}
                  productArea="local"
                  opportunity={{
                    title: opportunity.opportunity_title,
                    summary: opportunity.opportunity_description,
                    recommendation: opportunity.recommended_action || undefined,
                    category: opportunity.opportunity_type,
                    intent: opportunity.action_type,
                    priority: opportunity.priority,
                    evidence: [
                      `Supporting score: ${
                        opportunity.supporting_score ?? "Not available"
                      }`,
                      ...(opportunity.evidence || []),
                    ],
                  }}
                  accountContext={{
                    accountName:
                      accountContext?.display_name ||
                      accountContext?.business_name ||
                      accountContext?.account_name ||
                      undefined,
                    category: accountContext?.category || opportunity.opportunity_type,
                    location:
                      accountContext?.primary_geo_value ||
                      accountContext?.market ||
                      undefined,
                    differentiators: Array.isArray(
                      accountContext?.local_config?.differentiators
                    )
                      ? accountContext.local_config.differentiators
                      : undefined,
                  }}
                  onCompletionChange={handleLocalCompletionChange}
                />
              )}
              </>
              )}
            </div>
            </React.Fragment>
            );
          })}
        </div>
        )
      )}

      {isFocused && focusedOpportunities.length > focusedVisibleCount && (
        <button
          type="button"
          data-testid="oe-view-all-opportunities"
          aria-expanded={showAllRecommendations}
          onClick={() => setShowAllRecommendations((current) => !current)}
          style={{ marginTop: 12, border: "1px solid #93c5fd", borderRadius: 999, padding: "7px 11px", background: "#eff6ff", color: "#1d4ed8", fontSize: 12, fontWeight: 800, cursor: "pointer" }}
        >
          {showAllRecommendations ? "Show fewer" : "View all opportunities"}
        </button>
      )}

      </div>
      <div style={isFocused ? { order: 5 } : undefined}>
      {recentCompletedWork.length > 0 && (
        <details data-testid="oe-recent-completed-work" style={{ marginTop: 14 }}>
          <summary style={{ cursor: "pointer", color: "#64748b", fontSize: 12, fontWeight: 800 }}>Recently completed local work ({recentCompletedWork.length})</summary>
          <div style={{ ...styles.recentWorkList, marginTop: 9 }}>
            {recentCompletedWork.map((item) => (
              <div key={item.id} style={styles.recentWorkItem}>
                <p style={styles.recentWorkItemTitle}>{item.title || "Completed local action"}</p>
                <div style={styles.recentWorkMeta}><span>{formatCompletionDate(item.marked_complete_at)}</span><span>•</span><span>{formatActionType(item.action_type)}</span>{item.completed_by && <><span>•</span><span>{item.completed_by}</span></>}</div>
              </div>
            ))}
          </div>
        </details>
      )}

      {!loading && !errorMessage && visibleCompletedOpportunities.length > 0 && (
        <details style={{ marginTop: 18 }}>
          <summary style={{ cursor: "pointer", color: "#166534", fontSize: 15, fontWeight: 900, borderTop: "1px solid #cbd5e1", paddingTop: 14 }}>
            Completed Local Action Briefs ({visibleCompletedOpportunities.length})
          </summary>
          <p style={{ color: "#64748b", fontSize: 12, margin: "8px 0 12px" }}>
            Completed items are removed from the active opportunity queue. Use Mark as incomplete to return work to the active list.
          </p>
          <div style={styles.list}>
            {visibleCompletedOpportunities.map((opportunity) => {
              const actionTone = getLocalActionTone(opportunity);
              const completedBrief = completedLocalBriefs.find((brief) => brief.id === opportunity.id);
              return (
                <div key={opportunity.id} style={{ ...styles.item, background: "#f0fdf4" }}>
                  <div style={styles.itemHeader}>
                    <div style={styles.itemMain}>
                      <div style={styles.titleRow}>
                        <p style={styles.itemTitle}>{opportunity.opportunity_title}</p>
                        <span style={{ ...styles.badge, background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }}>Completed</span>
                      </div>
                      <p style={styles.itemDescription}>{opportunity.opportunity_description}</p>
                    </div>
                    <div style={{ ...styles.metaBox, ...actionTone.card }}>
                      <span style={styles.metaLabel}>Action Type</span>
                      <span style={{ ...styles.actionType, color: actionTone.textColor }}>{formatActionType(opportunity.action_type)}</span>
                    </div>
                  </div>
                  <OpportunityActionBriefPanel
                    accountId={accountId || ""}
                    runId={localRunId}
                    productArea="local"
                    opportunity={{
                      title: opportunity.opportunity_title,
                      summary: opportunity.opportunity_description,
                      recommendation: opportunity.recommended_action || undefined,
                      category: opportunity.opportunity_type,
                      intent: opportunity.action_type,
                      priority: opportunity.priority,
                      evidence: opportunity.evidence || [],
                    }}
                    accountContext={{
                      accountName: accountContext?.display_name || accountContext?.business_name || accountContext?.account_name || undefined,
                      category: accountContext?.category || opportunity.opportunity_type,
                      location: accountContext?.primary_geo_value || accountContext?.market || undefined,
                      differentiators: Array.isArray(accountContext?.local_config?.differentiators) ? accountContext.local_config.differentiators : undefined,
                    }}
                    initialActionBrief={completedBrief as ActionBrief}
                    onCompletionChange={handleLocalCompletionChange}
                  />
                </div>
              );
            })}
          </div>
        </details>
      )}
      </div>
      </div>
      </div></div>
    </section>
  );
}
